import { useCallback, useEffect, useRef, useState } from 'react'
import type RFB from '@novnc/novnc'
import { leases as leaseApi } from '@/api/endpoints'
import type { Lease } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Err } from '@/components/Err'
import { XK_ALT_L, XK_CONTROL_L, XK_ESCAPE, XK_SUPER_L, XK_TAB, typeText } from '@/domain/console'
import { cn } from '@/lib/utils'

// 그래픽 콘솔(D-1). 대화 상자 하나가 거의 전체 창이고, 안에 noVNC 캔버스와 도구 막대가 있다.
// 열리면 POST /leases/{id}/console 로 단회 URL 과 VNC 티켓을 받아 같은 origin 의 WebSocket 을 열고,
// RFB 인증은 브라우저와 게스트 사이에서 끝난다 — 여기서는 티켓을 화면에도 state 에도 두지 않는다.
//
// 키보드: 캔버스에 포커스가 있는 동안 noVNC 가 keydown/keyup 을 잡아 게스트로 보낸다(Ctrl-C/V, F1~F12, Alt-문자, Esc, Tab …).
// 그래서 Radix 의 Esc 닫기는 끈다. 브라우저·OS 가 먼저 먹는 조합(Ctrl-W/T/N, Alt-Tab, Win …)은 전체 화면 + Keyboard Lock
// (Chromium)으로만 게스트에 닿는다. 도구 막대의 고정 토글(Ctrl·Alt·Win)은 눌러 두고 다음 키가 올라올 때 푼다.
// 클립보드: QEMU VNC 는 게스트 안 vdagent 없이는 클립보드가 없어, `보내기` 가 문자마다 키 입력으로 타이핑한다.

type Sticky = 'ctrl' | 'alt' | 'super'
const STICKY: { key: Sticky; word: string; keysym: number; code: string }[] = [
  { key: 'ctrl', word: 'Ctrl', keysym: XK_CONTROL_L, code: 'ControlLeft' },
  { key: 'alt', word: 'Alt', keysym: XK_ALT_L, code: 'AltLeft' },
  { key: 'super', word: 'Win', keysym: XK_SUPER_L, code: 'MetaLeft' },
]

type KeyboardLock = { lock?: (keys?: string[]) => Promise<void>; unlock?: () => void }
function keyboardLock(): KeyboardLock | undefined {
  return (navigator as Navigator & { keyboard?: KeyboardLock }).keyboard
}

/** 상세 화면의 `콘솔` 칩과 그 대화 상자. 사용 중인 임대의 실행 중인 기계에만 있다. */
export function ConsoleChip({ lease, className, disabled = false }: { lease: Lease; className: string; disabled?: boolean }) {
  const [open, setOpen] = useState(false)
  if (lease.state !== 'active' || lease.vm?.state !== 'running') return null
  return (
    <>
      {/* 좁은 화면에는 칩이 없다 — 수식키 도구 막대도 전체 화면도 없고 축소된 화면을 터치로 조작할 수 없다. */}
      <button type="button" className={cn(className, 'max-sm:hidden')} disabled={disabled} onClick={() => setOpen(true)}>
        콘솔
      </button>
      {open ? <ConsoleDialog lease={lease} onClose={() => setOpen(false)} /> : null}
    </>
  )
}

type Status = 'connecting' | 'connected' | 'disconnected'

export function ConsoleDialog({ lease, onClose }: { lease: Lease; onClose: () => void }) {
  // 캔버스 자리는 ref 가 아니라 state 다: Radix 가 대화 상자를 한 박자 뒤에 붙이므로, 요소가 생긴 뒤에 연결이 시작돼야 한다.
  const [screen, setScreen] = useState<HTMLDivElement | null>(null)
  const frameRef = useRef<HTMLDivElement>(null)
  const rfbRef = useRef<RFB | null>(null)
  const [status, setStatus] = useState<Status>('connecting')
  const [error, setError] = useState<unknown>(null)
  const [held, setHeld] = useState<Set<Sticky>>(new Set())
  const [text, setText] = useState('')
  const [note, setNote] = useState<string | null>(null)
  const [fullscreen, setFullscreen] = useState(false)

  useEffect(() => {
    let cancelled = false
    let rfb: RFB | null = null
    const el = screen
    if (!el) return
    const run = async () => {
      try {
        const opened = await leaseApi.console(lease.id)
        const { default: RFBClass } = await import('@novnc/novnc')
        if (cancelled) return
        const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${opened.url}`
        rfb = new RFBClass(el, url, { credentials: { password: opened.vnc_ticket }, wsProtocols: ['binary'] })
        rfb.scaleViewport = true
        rfb.resizeSession = false
        rfb.focusOnClick = true
        rfb.addEventListener('connect', () => {
          setStatus('connected')
          rfb?.focus()
        })
        rfb.addEventListener('disconnect', () => setStatus('disconnected'))
        rfb.addEventListener('credentialsrequired', () => rfb?.sendCredentials({ password: opened.vnc_ticket }))
        rfbRef.current = rfb
      } catch (err) {
        if (!cancelled) setError(err)
      }
    }
    void run()
    return () => {
      cancelled = true
      rfbRef.current = null
      rfb?.disconnect()
      keyboardLock()?.unlock?.()
    }
  }, [lease.id, screen])

  // 고정 토글은 다음 키가 올라올 때 푼다 — 수식키 자체가 올라오는 것은 세지 않는다.
  useEffect(() => {
    const frame = frameRef.current
    if (!frame || held.size === 0) return
    const release = (e: KeyboardEvent) => {
      if (['Control', 'Alt', 'Meta', 'Shift'].includes(e.key)) return
      for (const m of STICKY) if (held.has(m.key)) rfbRef.current?.sendKey(m.keysym, m.code, false)
      setHeld(new Set())
    }
    frame.addEventListener('keyup', release, true)
    return () => frame.removeEventListener('keyup', release, true)
  }, [held])

  useEffect(() => {
    const onChange = () => {
      const on = document.fullscreenElement === frameRef.current
      setFullscreen(on)
      if (!on) keyboardLock()?.unlock?.()
    }
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  const toggleSticky = (m: (typeof STICKY)[number]) => {
    const rfb = rfbRef.current
    if (!rfb) return
    const next = new Set(held)
    if (next.has(m.key)) {
      rfb.sendKey(m.keysym, m.code, false)
      next.delete(m.key)
    } else {
      rfb.sendKey(m.keysym, m.code, true)
      next.add(m.key)
    }
    setHeld(next)
    rfb.focus()
  }

  const tap = (keysym: number, code: string) => {
    rfbRef.current?.sendKey(keysym, code)
    rfbRef.current?.focus()
  }

  const enterFullscreen = useCallback(async () => {
    const frame = frameRef.current
    if (!frame) return
    try {
      if (document.fullscreenElement === frame) {
        await document.exitFullscreen()
        return
      }
      await frame.requestFullscreen()
      await keyboardLock()?.lock?.()
      rfbRef.current?.focus()
    } catch {
      // 전체 화면이나 키 잠금을 브라우저가 거부했다. 창 안에서는 그대로 쓴다.
    }
  }, [])

  const send = () => {
    const rfb = rfbRef.current
    if (!rfb || !text) return
    const { skipped } = typeText(rfb, text)
    setNote(skipped > 0 ? `${skipped}글자는 보낼 수 없어 건너뛰었습니다.` : null)
    setText('')
    rfb.focus()
  }

  return (
    <Dialog open onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent
        aria-describedby={undefined}
        className="flex h-[92vh] w-[96vw] max-w-none flex-col gap-2 p-3 sm:max-w-none"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="pr-8">
          <DialogTitle className="flex items-baseline gap-2">
            <span>{lease.name} 콘솔</span>
            <span className="text-[11px] font-normal text-muted-foreground">
              {status === 'connecting' ? '연결 중' : status === 'disconnected' ? '연결이 끊겼습니다.' : ''}
            </span>
          </DialogTitle>
        </DialogHeader>
        <Err error={error} />
        <div ref={frameRef} className="flex min-h-0 flex-1 flex-col gap-2 bg-background">
          <div className="flex flex-wrap items-center gap-1.5">
            {STICKY.map((m) => (
              <Button
                key={m.key}
                type="button"
                size="xs"
                variant={held.has(m.key) ? 'default' : 'outline'}
                aria-pressed={held.has(m.key)}
                onClick={() => toggleSticky(m)}
                disabled={status !== 'connected'}
              >
                {m.word}
              </Button>
            ))}
            <Button type="button" size="xs" variant="outline" onClick={() => tap(XK_TAB, 'Tab')} disabled={status !== 'connected'}>
              Tab
            </Button>
            <Button type="button" size="xs" variant="outline" onClick={() => tap(XK_ESCAPE, 'Escape')} disabled={status !== 'connected'}>
              Esc
            </Button>
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={() => {
                rfbRef.current?.sendCtrlAltDel()
                rfbRef.current?.focus()
              }}
              disabled={status !== 'connected'}
            >
              Ctrl+Alt+Del
            </Button>
            <Button type="button" size="xs" variant="outline" onClick={() => void enterFullscreen()} disabled={status !== 'connected'}>
              {fullscreen ? '창으로' : '전체 화면'}
            </Button>
            <form
              className="ml-auto flex items-center gap-1.5"
              onSubmit={(e) => {
                e.preventDefault()
                send()
              }}
            >
              <Input
                aria-label="보낼 텍스트"
                className="h-6 w-56 text-xs"
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={status !== 'connected'}
              />
              <Button type="submit" size="xs" variant="outline" disabled={status !== 'connected' || !text}>
                보내기
              </Button>
            </form>
          </div>
          {note ? <div className="text-[11px] text-muted-foreground">{note}</div> : null}
          <div ref={setScreen} className="min-h-0 flex-1 overflow-hidden rounded-md bg-black" />
        </div>
      </DialogContent>
    </Dialog>
  )
}
